import { Text } from '@components/primitives/Text'

export default function InformatedConsentScroller({
  onScroll,
}: {
  onScroll?: () => void
}) {
  return (
    <div
      className='grid max-h-72 grid-cols-1 gap-4 overflow-y-scroll bg-lightGrey700 p-4'
      onScroll={onScroll}
    >
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'What is Power’s Local Patients Registry?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry is a free and secure tool that allows willing volunteers to be invited to participate in trials by researchers.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'How does it work?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'If you choose to opt-in to the Local Patients Registry, researchers will be able to see an anonymized version of your Power Patient Profile. From here, these researchers can invite you to participate in their trial, if — based on your anonymous profile — they deem that you might be a fit.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'We never directly share your contact details with researchers. Instead, if a researcher invites your anonymous profile to contact them about their trial, you are given the choice to contact them.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'You can opt-out of the Local Patients Registry at any point, following which researchers would no longer be able to view your anonymous profile or invite you to contact them.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={
            'Are there any benefits, costs or risks of joining Power’s Local Patients Registry as a Volunteer?'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'By contributing to scientific progress you may help improve broader society’s health and well-being. We have a deep need for volunteers in clinical research, and many people who would like to join are unaware of how to do so.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Further, participating in the Power Local Patients Registry may:'
          }
        />
        <ul className='list-inside list-disc'>
          <li>
            <Text
              className='inline'
              styleName='p-small'
              value={'Reduce the time you spend looking for research studies'}
            />
          </li>
          <li>
            <Text
              className='inline'
              styleName='p-small'
              value={
                'Improve your chances of being matched with research studies that interest you'
              }
            />
          </li>
          <li>
            <Text
              className='inline'
              styleName='p-small'
              value={
                'Help you learn more about your community’s research and resource'
              }
            />
          </li>
        </ul>
        <Text
          element='p'
          styleName='p-small'
          value={
            'Notably, there is also a chance that you do not realize any benefit by joining Power’s Local Patients Registry. Power’s Local Patients Registry is unable to guarantee that using this tool will result in you being contacted by a researcher for any specific research study, either now or in the future.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'There is a very small risk that some of your profile information may be released without your permission, but Power’s Local Patients Registry takes many steps to keep your personal information private, secure, and confidential until you explicitly opt to share these details with researchers who express interest in your anonymous profile. You may get frequent emails from researchers using Power’s Local Patients Registry when your anonymous profile is ‘matched’ with their research studies. If you feel that you are getting too many emails, you may always remove yourself from Power’s Local Patients Registry. There is no expected cost to participate in Power’s Local Patients Registry, and no compensation or reimbursement will be made available.'
          }
        />
        <Text element='h3' styleName='p-small-strong' value={'Who can join?'} />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Anyone who lives in the United States can join Power’s Local Patients Registry as a Volunteer. You do not have to be diagnosed with a health condition to be a Power’s Local Patients Registry Volunteer. If you decide to join, you will be asked if you are registering for yourself or someone else as their guardian when submitting the Volunteer registration form. A parent, legal guardian, or caregiver can register someone under the age of 18. They can also register an adult that is not able to enter his or her own information.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'What do I have to do to join?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Joining Power’s Local Patients Registry is easy. Choosing to opt in will automatically sync your Power Patient Profile with the Registry, so no further data entry is required. All of this information will be stored securely and will be kept confidential as described below and in the Power’s Privacy Policy.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Your Power Local Patients Registry profile is synonymous with your Power Account, and is tied to your email address. Contact Power’s Local Patients Registry immediately if you think someone else has gained access to your account via your email, or if you have any other security concerns.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Beyond removing yourself from the Local Patients Registry, at any point you can request that the entirety of your Power Patient Profile is deleted.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'What happens once I register?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Once you join Power’s Local Patients Registry, your non-identifiable information, including your country of residence & county, becomes part of a pool of data. Researchers search through this pool when looking for people to contact about their studies. These studies will already be approved for posting on ClinicalTrials.gov.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'If a researcher chooses to invite you to their trial, you will receive an email notification. If you choose to unsubscribe from these emails, you will automatically be opted-out of the Registry more broadly.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'To limit access to your information, researchers will not directly receive your contact details. Instead, all initial contact will come from the aforementioned invitation email which will be sent to the email address connected to your Power Patient Profile. After receiving an invitation from a researcher, you will be able to view further details regarding their study.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'If you are interested, you can then choose to ‘contact’ said study, at which point you will be sharing your contact details with the researcher.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Remember that by joining Power’s Local Patients Registry you do not have to take part in any study. You may choose not to respond to a researcher message at any time. In any such case, your information would not be shared with the researcher.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry will perform quality and evaluation metrics on an on-going basis. Part of that process may involve collecting non-identifiable information about your contact and enrollment status in researchers’ studies. This will help us see how useful this tool is to Power’s Local Patients Registry’s researchers and institutions.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'Power’s Local Patients Registry Disclaimer'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry does not promise that researchers will contact you about studies through the Power’s Local Patients Registry system. Power’s Local Patients Registry does not support any specific research, research institution, or study. Receiving study recruitment invitations does not mean Power’s Local Patients Registry has reviewed the study, although all researchers on the platform will have verified their affiliation with at least one study approved for listing on clinicaltrials.gov. It also does not mean that Power’s Local Patients Registry recommends that you participate in that study.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'How is my information protected?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry respects your privacy. It takes privacy very seriously. Your personal information will be sent and stored in a secure system. Please see the Power’s Privacy Policy for more details.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry will never sell, rent, or lease your personal information.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry will only give your contact information to researchers when you opt to contact them. Once you have said it is okay to release your contact information, Power’s Local Patients Registry no longer manages the connection between you and your research study. Power’s Local Patients Registry is not responsible for how your contact information is handled by the research study.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'If you ask Power’s Local Patients Registry to release your information, and you have questions or concerns about a particular study, you can contact the researcher and/or that study’s Institutional Review Board.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={
            'What if I change my mind about being part of Power’s Local Patients Registry?'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'You are able to remove your personal information from Power’s Local Patients Registry at any time by going to your profile and selecting this option. If you have already released your information to some researchers or study databases, removing yourself from Power’s Local Patients Registry does not mean that your contact information will be removed from these other places. It only means that future researchers and studies will no longer have access to your profile when looking for people who match their study in the Power’s Local Patients Registry.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'Who is involved in Power’s Local Patients Registry?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Power’s Local Patients Registry is a service operated and financed by Power Life Sciences Inc., the same company operating www.withpower.com.'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={
            'Does Power’s Local Patients Registry provide health care services?'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'No. Power’s Local Patients Registry does not provide any medical or healthcare products, services or advice. If you have any questions about your health care or medical condition, please see your regular doctor.'
          }
        />
        <Text element='h3' styleName='p-small-strong' value={'AGREEMENT'} />
        <Text
          element='p'
          styleName='p-small'
          value={
            'By agreeing to this Volunteer Agreement, you willingly agree to let Power’s Local Patients Registry use and share your de-identified data as described here and in the Power Privacy Policy. You also agree to let Power’s Local Patients Registry access, keep or share your registration information if required to do so by law or in the good faith belief that it is necessary to:'
          }
        />
        <ul className='list-inside list-disc'>
          <li>
            <Text
              className='inline'
              styleName='p-small'
              value={
                'Enforce the terms of this Volunteer Agreement or the site’s Terms of Use'
              }
            />
          </li>
          <li>
            <Text
              className='inline'
              styleName='p-small'
              value={'Respond to your requests for customer service'}
            />
          </li>
          <li>
            <Text
              className='inline'
              styleName='p-small'
              value={'Comply with federal, state or local legal process'}
            />
          </li>
        </ul>
        <Text
          element='p'
          styleName='p-small'
          value={
            'By agreeing to this Volunteer Agreement, you willingly agree to let Power’s Local Patients Registry use and share your de-identified data as described here and in the Power Privacy Policy. You also agree to let Power’s Local Patients Registry access, keep or share your registration information if required to do so by law or in the good faith belief that it is necessary to:'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'If you agree, your profile will be added to the Power’s Local Patients Registry volunteer registry as soon as you submit this form.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'Thank you for learning more about Power’s Local Patients Registry!'
          }
        />
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <Text
          element='h3'
          styleName='p-small-strong'
          value={'Do you have questions or concerns?'}
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'For general questions, please email Power’s Local Patients Registry at hello@withpower.com.'
          }
        />
        <Text
          element='p'
          styleName='p-small'
          value={
            'For more information about giving consent or about your rights as a person on Power’s Local Patients Registry, please feel free to call the Power’s Local Patients Registry Institutional Review Board of Record Office at info@withpower.com'
          }
        />
      </div>
    </div>
  )
}
